import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { EllipsisVerticalIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { Fragment, useMemo } from "react";
import StatusIndicator from "../Shared/StatusIndicator";
import PrimaryButton from "../Shared/PrimaryButton";
import { getOriginalTimeDisplay } from "../../utility/timezoneUtils";
import { useUserPreferences } from "../../hooks/useUserPreferences";

const AppointmentHistoryRow = ({
  appointment,
  onView,
  onEdit,
  onUpdateStatus,
  onCancel,
}) => {
  const { is12HourClock } = useUserPreferences();

  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showOriginalTime =
    appointment.timeZoneId && appointment.timeZoneId !== systemTimezone;

  const originalTimeDisplay = useMemo(() => {
    return getOriginalTimeDisplay(
      appointment.startTime,
      appointment.timeZoneId,
      is12HourClock
    );
  }, [
    appointment.startTime,
    appointment.timeZoneId,
    showOriginalTime,
    systemTimezone,
    is12HourClock,
  ]);

  const statusId = appointment.statusId;
  const isPast = appointment.rawStartTime
    ? new Date(appointment.rawStartTime).getTime() < Date.now()
    : false;
  const canCancel = statusId !== "cancelled" && statusId !== "completed";

  const inlineAction = (() => {
    if (statusId === "pending" && !isPast) {
      return {
        label: "Confirm",
        onClick: () => onUpdateStatus(appointment.id, "confirmed"),
      };
    }
    if (statusId === "confirmed" && isPast) {
      return {
        label: "Mark complete",
        onClick: () => onUpdateStatus(appointment.id, "completed"),
      };
    }
    return null;
  })();

  const renderInlineButton = () =>
    inlineAction ? (
      <PrimaryButton
        colour="white"
        onClick={inlineAction.onClick}
        className="text-xs px-2 py-1"
      >
        {inlineAction.label}
      </PrimaryButton>
    ) : null;

  const renderMenu = () => (
    <Menu as="div" className="relative flex-none">
      <MenuButton className="-m-2.5 block p-2.5 text-ink-500 hover:text-ink-900">
        <span className="sr-only">Open options</span>
        <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          anchor="bottom end"
          className="z-10 w-36 rounded-md bg-white py-2 shadow-lg ring-1 ring-ink-100 focus:outline-none"
        >
          <MenuItem>
            {({ close }) => (
              <button
                type="button"
                onClick={() => {
                  onView(appointment);
                  close();
                }}
                className="block w-full px-3 py-1 text-left text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
              >
                View details
              </button>
            )}
          </MenuItem>
          <MenuItem>
            {({ close }) => (
              <button
                type="button"
                onClick={() => {
                  onEdit(appointment);
                  close();
                }}
                className="block w-full px-3 py-1 text-left text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
              >
                Edit
              </button>
            )}
          </MenuItem>
          {canCancel && (
            <>
              <div className="my-1 h-px bg-cream-200" />
              <MenuItem>
                {({ close }) => (
                  <button
                    type="button"
                    onClick={() => {
                      onCancel(appointment);
                      close();
                    }}
                    className="block w-full px-3 py-1 text-left text-sm/6 text-signal-red-600 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                  >
                    Cancel appointment
                  </button>
                )}
              </MenuItem>
            </>
          )}
        </MenuItems>
      </Transition>
    </Menu>
  );

  return (
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-y-4 sm:gap-y-0 sm:gap-x-6 py-5">
      <div className="flex min-w-0 gap-x-4">
        <CalendarDaysIcon aria-hidden="true" className="size-6" />
        <div className="min-w-0 flex-auto">
          <div className="flex items-start justify-between sm:justify-start">
            <div className="flex items-start gap-x-3">
              <p className="text-sm pt-1 font-semibold text-ink-900">
                {appointment.startTime}
              </p>
              <div className="hidden sm:block">
                <StatusIndicator
                  colour={appointment.statusColour}
                  label={appointment.status}
                />
              </div>
            </div>
            <div className="sm:hidden">{renderMenu()}</div>
          </div>
          {originalTimeDisplay && (
            <p className="text-xs text-ink-500 mt-0.5">
              {originalTimeDisplay}
            </p>
          )}
          <div className="mt-1 flex items-center sm:hidden">
            <StatusIndicator
              colour={appointment.statusColour}
              label={appointment.status}
            />
          </div>
          {appointment.services?.length > 0 && (
            <p className="mt-1 text-xs text-ink-600 truncate">
              {appointment.services.map((s) => s.name).join(", ")}
            </p>
          )}
          <div className="mt-1 flex items-center text-xs/5 text-ink-500 max-w-full">
            <div className="flex-shrink-0 w-5 h-5">
              <MapPinIcon aria-hidden="true" className="size-4 text-ink-400" />
            </div>
            <p className="truncate">{appointment.address}</p>
          </div>
          {inlineAction && <div className="mt-2 sm:hidden">{renderInlineButton()}</div>}
        </div>
      </div>

      <div className="hidden sm:flex items-center gap-x-4">
        {inlineAction && (
          <div className="flex items-center gap-x-2">{renderInlineButton()}</div>
        )}
        {renderMenu()}
      </div>
    </div>
  );
};

export default AppointmentHistoryRow;
